import { useCallback, useEffect, useState } from 'react'
import { useBreakpoint, useViewportWidth, type Breakpoint } from './useBreakpoint'
import {
  DOCK_DEFAULT_WIDTH,
  DOCK_MAX_WIDTH,
  DOCK_MIN_WIDTH,
  readDockPreference,
  writeDockPreference,
  type DockChoice,
} from './dockPreference'

export { DOCK_DEFAULT_WIDTH, DOCK_MIN_WIDTH, DOCK_MAX_WIDTH, clampDockWidth } from './dockPreference'

/** The collapsed rail: an orb button and a vertical label, nothing else. */
export const DOCK_RAIL_WIDTH = 60

/**
 * The other two fixed grid tracks, in the arithmetic that decides how much room
 * is left for the dock.
 *
 * ⚠️ These duplicate `marva-tokens.css`'s `.mv-app` track list. The duplication
 * is checked rather than trusted: `tokens-fidelity.test.ts` imports these and
 * asserts the stylesheet still agrees, so a rail that widens in CSS cannot
 * leave this arithmetic quietly wrong.
 */
export const RAIL_TRACK_WIDTH = 82
export const CTX_TRACK_WIDTH = 312

/**
 * The narrowest the canvas is allowed to become before the dock has to give way.
 *
 * `.mv-canvas` carries 30px of horizontal padding each side, so this leaves a
 * 300px content column — about the narrowest anything else in the design is
 * asked to render into.
 *
 * ⚠️ **The failure this prevents is CLIPPING, not a sideways scroll.**
 * `.mv-root` is `overflow:hidden` (`marva-tokens.css:47`), so nothing ever
 * scrolls: the overrun is silently amputated. At 900px with the context panel
 * open and a restored 640px dock the tracks resolve to 82 + 60 + 312 + 640 =
 * 1094 in a 900px window — 194px of the dock is cut off and unreachable, and
 * the canvas floors at 60px, which is its own padding and therefore a
 * ZERO-width content column. That is worse than a scrollbar, because a
 * scrollbar at least offers to show you the rest. Verified in a real browser by
 * `npm run check:dock-geometry` scenarios 10–13; an earlier version of this
 * comment claimed the page scrolled, and the geometry script disproved it.
 */
export const MIN_CANVAS_WIDTH = 360

export interface DockLayout {
  band: Breakpoint
  /** Drawn as a fixed overlay rather than a grid column (`narrow` only). */
  overlay: boolean
  /** What the dock actually is right now — merchant's choice or auto-collapsed. */
  collapsed: boolean
  /**
   * How much of the viewport's right edge the dock occupies IN FLOW — what
   * `--dockw` carries. The rail width when collapsed, and **0 in the overlay
   * band**, where the dock floats above the canvas and owns none of it.
   */
  trackWidth: number
  /** The expanded width the merchant dragged to, kept across an auto-collapse. */
  width: number
  /**
   * The widest the dock may be in this window right now — the design's 640
   * ceiling, or whatever is left after the rail, the context panel and a
   * usable canvas, whichever is smaller. The grip clamps to it.
   */
  maxWidth: number
  /** The grip is offered. False in the overlay band, where width does nothing. */
  resizable: boolean
  /**
   * The collapsed rail stands down: it is an overlay, and a drawer is open
   * under it, so it would be sitting on the drawer's close button.
   */
  behindDrawer: boolean
  /** The chevron and the rail button. */
  setCollapsed: (collapsed: boolean) => void
  /** The grip, mid-drag. Clamped by the caller to the design's bounds. */
  setWidth: (px: number) => void
}

/**
 * The dock's layout state: how wide it is, whether it is collapsed, and whether
 * it is a grid column or an overlay.
 *
 * The rule that shapes all of it: **auto-collapse is derived, never stored.**
 * What the merchant chose — collapsed or not, and the width they dragged to —
 * lives in state; a drawer opening in the middle band adds a collapse on top of
 * it without touching it. "Closing the drawer restores what the merchant had"
 * therefore costs nothing and cannot drift: there is no restore step to get
 * wrong, because nothing was overwritten. A version that wrote `collapsed=true`
 * on open and `false` on close would forget a merchant who had collapsed the
 * dock themselves, and would reset a dragged width to 400px.
 *
 * The same rule governs what is persisted: the merchant's choice, never the
 * derived state (`dockPreference.ts`).
 */
export function useDockLayout(openDrawerCount: number, contextPanelOpen = false): DockLayout {
  const band = useBreakpoint()
  const viewport = useViewportWidth()
  const overlay = band === 'narrow'

  // ⚠️ Read ONCE, lazily, at first render — not restored in an effect. An
  // effect would render one frame of the default geometry and then jump, which
  // is a visible flinch on every load.
  const [restored] = useState(readDockPreference)
  // The merchant's choice AND the layout they made it in, held together. What
  // was read is kept verbatim: it is scoped below, at the read, and re-written
  // unchanged — so opening the dock on a phone cannot destroy a choice made on
  // a desktop.
  const [choice, setChoice] = useState<DockChoice | null>(restored.choice)
  const [width, setWidthState] = useState(restored.width)
  // Set when the merchant expands the dock by hand *while* a drawer has
  // auto-collapsed it. Without it the chevron and the rail button would be dead
  // controls for as long as the drawer stayed open.
  const [overrideAuto, setOverrideAuto] = useState(false)

  const drawerOpen = openDrawerCount > 0

  // ≥1600 the two coexist and a drawer never moves the dock; <900 the dock is
  // an overlay, so a drawer does not collapse it either. Only `mid` collapses.
  const autoCollapsed = band === 'mid' && drawerOpen && !overrideAuto

  // ── Scoped at the read. A choice made in the other layout is not this
  // layout's choice: "expanded", chosen on a screen wide enough for a column,
  // would restore as a fixed overlay covering the phone it is now on. `null`
  // then means "they have not said HERE", and the band's own default applies.
  const merchantCollapsed = choice && choice.overlay === overlay ? choice.collapsed : null

  // How much of the window is left once the rail, the context panel and a
  // usable canvas have taken theirs. `0` viewport (SSR, or a stub) means "not
  // measured", and an unmeasured window must not shrink anything.
  const room = viewport > 0 ? viewport - RAIL_TRACK_WIDTH - (contextPanelOpen ? CTX_TRACK_WIDTH : 0) - MIN_CANVAS_WIDTH : Number.POSITIVE_INFINITY
  // In the overlay band the dock is `position:fixed` at `min(360px,86vw)` and
  // owns no track, so none of this applies to it.
  const maxWidth = overlay ? DOCK_MAX_WIDTH : Math.min(DOCK_MAX_WIDTH, Math.max(DOCK_MIN_WIDTH, room))
  // There is not even room for the narrowest usable dock. Collapsing is derived
  // from the window, exactly like the drawer collapse — never stored, so the
  // merchant's own width comes back the moment the window does.
  const noRoom = !overlay && room < DOCK_MIN_WIDTH

  const base = merchantCollapsed ?? overlay
  const collapsed = base || autoCollapsed || noRoom

  // The override belongs to one drawer EPISODE — from the first drawer opening
  // to the last one closing — and is dropped whenever that episode starts or
  // ends.
  //
  // ⚠️ Keyed on `drawerOpen`, a boolean, not on the count: opening a second
  // drawer while one is already open (Knowledge, routinely) does NOT drop it.
  // That is deliberate and it is the better behaviour — having expanded the
  // dock by hand, the merchant should not have it snatched away because they
  // opened a second drawer in the same episode. An earlier comment here claimed
  // the opposite of what the code does.
  useEffect(() => setOverrideAuto(false), [drawerOpen])

  // Persist the merchant's choice, never the derived collapse, and never a
  // band tag this render happens to be in — `choice` carries its own.
  //
  // ⚠️ `choice`, not `merchantCollapsed`. The scoped value is what this layout
  // should *apply*; the stored value is what the merchant *said*. Writing the
  // scoped one turned "not yours to apply" into "deleted", which destroyed a
  // desktop preference the first time the dock was opened on a phone.
  useEffect(() => {
    if (!choice && width === DOCK_DEFAULT_WIDTH) return
    writeDockPreference({ width, choice })
  }, [choice, width])

  // Stamped with the layout the merchant is actually looking at when they
  // choose — the only moment that reading is true.
  const setCollapsed = useCallback(
    (next: boolean) => {
      setChoice({ collapsed: next, overlay })
      if (!next) setOverrideAuto(true)
    },
    [overlay]
  )

  const setWidth = useCallback((px: number) => setWidthState(px), [])

  return {
    band,
    overlay,
    collapsed,
    // ⚠️ 0 in the overlay band, and that is load-bearing beyond the dock: every
    // right-hand drawer is inset by `--dockw` so it stops at the dock's left
    // edge instead of covering it (`marva-overrides.css`). A floating dock owns
    // none of the viewport, so a phone-width drawer must not keep a gutter for
    // it — which is the live bug in the v6 prototype, where `applyDock`'s
    // inline write defeats its own `@media (max-width:900px){--dockw:0px}`.
    // ⚠️ `min(width, maxWidth)`, applied AT USE. The stored width stays
    // whatever the merchant chose, so opening on a narrow window does not
    // permanently shrink a width they picked on a big screen — the same
    // separation as the collapse choice and its band tag.
    trackWidth: overlay ? 0 : collapsed ? DOCK_RAIL_WIDTH : Math.min(width, maxWidth),
    width,
    maxWidth,
    resizable: !overlay,
    behindDrawer: overlay && collapsed && drawerOpen,
    setCollapsed,
    setWidth,
  }
}
